import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

export default function Sellers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const sortBy = searchParams.get('sortBy') || 'sales';

  const { data, isLoading } = useQuery({
    queryKey: ['sellers', page, search, sortBy],
    queryFn: () => api.get(`/sellers?page=${page}&limit=20&search=${search}&sortBy=${sortBy}`),
  });

  const sellers = (data as any)?.data?.sellers || [];
  const pagination = (data as any)?.data?.pagination;

  const updateParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== 'page') newParams.set('page', '1');
    setSearchParams(newParams);
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-western text-4xl text-cream mb-2">Our Sellers</h1>
          <p className="text-text-secondary">Discover trusted merchants in the Outlaw Oasis marketplace</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="card p-6 sticky top-24">
              <h2 className="font-semibold text-cream mb-4">Filters</h2>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm text-text-secondary mb-2">Search</label>
                <input
                  type="text"
                  className="input text-sm"
                  placeholder="Search sellers..."
                  value={search}
                  onChange={(e) => updateParam('search', e.target.value)}
                />
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm text-text-secondary mb-2">Sort By</label>
                <select
                  className="input text-sm"
                  value={sortBy}
                  onChange={(e) => updateParam('sortBy', e.target.value)}
                >
                  <option value="sales">Best Selling</option>
                  <option value="rating">Top Rated</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Sellers Grid */}
          <div className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card animate-pulse overflow-hidden">
                    <div className="h-24 bg-leather" />
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-leather shrink-0" />
                        <div className="space-y-2 flex-1">
                          <div className="h-4 bg-leather rounded w-2/3" />
                          <div className="h-3 bg-leather rounded w-1/3" />
                        </div>
                      </div>
                      <div className="h-3 bg-leather rounded w-full" />
                      <div className="h-3 bg-leather rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sellers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-text-secondary mb-4">No sellers found</p>
                {search && (
                  <button
                    onClick={() => setSearchParams({})}
                    className="text-gold hover:text-gold-bright"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sellers.map((seller: any) => (
                    <Link
                      key={seller.id}
                      to={`/sellers/${seller.storeSlug}`}
                      className="card group overflow-hidden flex flex-col"
                    >
                      {/* Banner */}
                      <div className="h-24 bg-leather relative overflow-hidden">
                        {seller.storeBannerUrl ? (
                          <img
                            src={seller.storeBannerUrl}
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-gold/20 font-western text-3xl">OO</span>
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="p-4 flex flex-col flex-1">
                        {/* Logo + Name */}
                        <div className="flex items-center gap-3 mb-3 -mt-8 relative">
                          <div className="w-14 h-14 rounded-full bg-leather-dark border-2 border-gold/30 overflow-hidden shrink-0 shadow-md">
                            {seller.storeLogoUrl ? (
                              <img
                                src={seller.storeLogoUrl}
                                alt={seller.storeName}
                                className="w-full h-full object-cover"
                              />
                            ) : seller.user?.avatarUrl ? (
                              <img
                                src={seller.user.avatarUrl}
                                alt={seller.user.username}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-leather">
                                <span className="text-gold font-western text-lg">
                                  {seller.storeName?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="pt-6 min-w-0">
                            <h3 className="font-semibold text-cream group-hover:text-gold transition-colors truncate">
                              {seller.storeName}
                            </h3>
                            {seller.user?.username && (
                              <p className="text-text-muted text-xs">@{seller.user.username}</p>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        {seller.storeDescription && (
                          <p className="text-text-secondary text-sm mb-4 line-clamp-2 flex-1">
                            {seller.storeDescription}
                          </p>
                        )}

                        {/* Stats */}
                        <div className="mt-auto pt-3 border-t border-gold/10 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-1 text-text-muted">
                              <span className="text-gold">&#9733;</span>
                              <span>
                                {seller.ratingAverage > 0
                                  ? `${seller.ratingAverage.toFixed(1)} (${seller.ratingCount})`
                                  : 'No ratings'}
                              </span>
                            </div>
                            <div className="text-text-muted text-sm">
                              {seller.totalSales > 0 ? (
                                <span>{seller.totalSales.toLocaleString()} sold</span>
                              ) : (
                                <span>New seller</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-text-muted">
                            <div className="flex items-center gap-1">
                              <span>&#128172;</span>
                              <span>{(seller.forumStats?.postCount ?? 0).toLocaleString()} posts</span>
                            </div>
                            <div className={`flex items-center gap-1 font-medium ${
                              (seller.forumStats?.reputation ?? 0) > 0
                                ? 'text-green-400'
                                : (seller.forumStats?.reputation ?? 0) < 0
                                  ? 'text-red-400'
                                  : 'text-text-muted'
                            }`}>
                              <span>Rep:</span>
                              <span>{(seller.forumStats?.reputation ?? 0) > 0 ? '+' : ''}{seller.forumStats?.reputation ?? 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => updateParam('page', String(page - 1))}
                      disabled={page <= 1}
                      className="btn-secondary py-2 px-4 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-text-secondary px-4">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => updateParam('page', String(page + 1))}
                      disabled={page >= pagination.totalPages}
                      className="btn-secondary py-2 px-4 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
